import { D, G } from "@mobily/ts-belt";
import PropTypes from "prop-types";
import * as React from "react";
import useCategoryMap from "../hooks/useCategoryMap";
import s from "./SelectedCategories.module.css";

const CloseIcon = () => (
  <svg
    viewBox="0 0 24 24"
    fill="none"
    stroke="currentColor"
    strokeWidth="2.5"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <line x1="18" y1="6" x2="6" y2="18" />
    <line x1="6" y1="6" x2="18" y2="18" />
  </svg>
);

function flattenCategories(categories, path = []) {
  if (!categories) {
    return [];
  }

  return Object.values(categories).flatMap((category) => {
    const newPath = [...path, category.id];
    const item = {
      id: category.id,
      name: category.name,
      path: newPath,
    };

    return Array.isArray(category.subcategories) &&
      category.subcategories.length > 0
      ? [item, ...flattenCategories(category.subcategories, newPath)]
      : [item];
  });
}

function flattenFilterCategories(categories, resultSet = new Set()) {
  if (D.isEmpty(categories) || G.isBoolean(categories)) {
    return resultSet;
  }

  Object.entries(categories).forEach(([key, value]) => {
    resultSet.add(Number(key));
    flattenFilterCategories(value, resultSet);
  });

  return resultSet;
}

function deepDelete(path) {
  return (obj) => {
    if (D.isEmpty(obj)) {
      return obj;
    }

    const [key, ...rest] = path;

    if (rest.length === 0) {
      return D.deleteKey(obj, key);
    }

    const value = obj[key];

    if (G.isObject(value)) {
      const [first] = rest;

      if (D.values(value).length === 1 && value[first]) {
        return D.deleteKey(obj, key);
      }

      return {
        ...obj,
        [key]: deepDelete(rest)(value),
      };
    }

    return obj;
  };
}

export default function SelectedCategories({ filter, setFilter }) {
  const categoryMap = useCategoryMap();

  const flattenedCategories = React.useMemo(
    () => flattenCategories(Object.values(categoryMap)),
    [categoryMap],
  );

  const flattenedFilterCategories = React.useMemo(
    () => flattenFilterCategories(filter.category),
    [filter.category],
  );

  const chips = flattenedCategories.reduce((result, { id, name, path }) => {
    if (!flattenedFilterCategories.has(id)) {
      return result;
    }

    return [...result, { id, name, path }];
  }, []);

  if (chips.length === 0) {
    return null;
  }

  return (
    <div className={s.list} role="list" aria-label="Active category filters">
      {chips.map(({ id, name, path }) => (
        <span key={id} className={s.chip} role="listitem">
          {name}
          <button
            type="button"
            className={s.deleteBtn}
            onClick={() => {
              setFilter(D.update("category", deepDelete(path)));
            }}
            aria-label={`Remove ${name} filter`}
          >
            <CloseIcon />
          </button>
        </span>
      ))}
    </div>
  );
}

SelectedCategories.propTypes = {
  filter: PropTypes.shape({
    category: PropTypes.object,
  }).isRequired,
  setFilter: PropTypes.func.isRequired,
};
